import React, { useLayoutEffect, useRef, useState } from "react";
import { FireflyVisual } from "@/features/petFireflies/FireflyVisual";
import { hashString } from "@/features/petFireflies/flightGeometry";
import { HeaderExcursion } from "./headerExcursion";
import { PetHeaderFireflyPlacement } from "./types";
import cls from "./placedFirefly.module.scss";

export function PlacedFirefly({
  firefly,
  canvasWidth,
  canvasHeight,
  idle,
  excursion,
}: {
  firefly: PetHeaderFireflyPlacement;
  canvasWidth: number;
  canvasHeight: number;
  idle: boolean;
  excursion: HeaderExcursion | null;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const lastTransform = useRef("none");
  const [returning, setReturning] = useState(false);
  const visiting = idle && Boolean(excursion);
  const { x = 0, y = 0, viaX = 0, viaY = 0 } = excursion ?? {};

  useLayoutEffect(() => {
    const node = ref.current;
    const from = lastTransform.current;
    lastTransform.current = "none";
    setReturning(false);
    if (
      !node?.animate ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    let animation: Animation | undefined;
    if (visiting) {
      animation = node.animate(
        [
          { transform: "translate(0px, 0px)", offset: 0 },
          { transform: `translate(${viaX}px, ${viaY}px)`, offset: 0.2 },
          { transform: `translate(${x}px, ${y}px)`, offset: 0.4 },
          { transform: `translate(${x}px, ${y}px)`, offset: 0.65 },
          { transform: `translate(${viaX}px, ${viaY}px)`, offset: 0.8 },
          { transform: "translate(0px, 0px)", offset: 1 },
        ],
        {
          duration: 12_000 + (hashString(firefly.id) % 4000),
          iterations: Infinity,
          easing: "ease-in-out",
        }
      );
    } else if (from !== "none" && from !== "matrix(1, 0, 0, 1, 0, 0)") {
      setReturning(true);
      animation = node.animate(
        [{ transform: from }, { transform: "translate(0px, 0px)" }],
        { duration: 900, easing: "ease-out" }
      );
      animation.onfinish = () => setReturning(false);
    }
    return () => {
      lastTransform.current = getComputedStyle(node).transform || "none";
      if (animation) {
        animation.onfinish = null;
        animation.cancel();
      }
    };
  }, [visiting, x, y, viaX, viaY, firefly.id]);

  return (
    <span
      aria-hidden="true"
      className={cls.placed}
      data-firefly-placement={firefly.id}
      data-firefly-resting={!visiting && !returning}
      style={{
        left: firefly.x * canvasWidth,
        top: firefly.y * canvasHeight,
        width: firefly.width * canvasWidth,
        height: firefly.width * canvasWidth,
        zIndex: firefly.stackOrder,
      }}
    >
      <span ref={ref} className={cls.excursion} data-firefly-excursion>
        <FireflyVisual
          active={visiting || returning}
          gaze={visiting ? excursion?.gaze : undefined}
        />
      </span>
    </span>
  );
}
